import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Stack,
  Typography,
  Paper,
  Toolbar,
  InputAdornment,
  Snackbar,
  Alert,
} from "@mui/material";

import { DataGrid } from "@mui/x-data-grid";
import SearchIcon from "@mui/icons-material/Search";
import AddIcon from "@mui/icons-material/Add";

import axios from "axios";
import Swal from "sweetalert2";
import ActionsMenu from "./ActionsMenu";

export default function Modules() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);

  const [search, setSearch] = useState("");

  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);

  const [form, setForm] = useState({
    module_code: "",
    module_name: "",
    icon_name: "",
    route_path: "",
    display_order: 1,
    is_active: true,
  });

  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [rowCount, setRowCount] = useState(0);

  const user = JSON.parse(sessionStorage.getItem("user"));

  const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

  const clientId = user?.client_id;
  const collegeId = user?.college_id;

  // ---------------------------------------------------------
  // FETCH
  // ---------------------------------------------------------

  const fetchData = async (
    currentPage = page,
    currentPageSize = pageSize,
    currentSearch = search
  ) => {
    setLoading(true);

    try {
      const { data } = await axios.get(
        `${API_BASE_URL}/api/rbacall/modules/${clientId}/${collegeId}`,
        {
          params: {
            page: currentPage + 1,
            pageSize: currentPageSize,
            search: currentSearch,
          },
        }
      );

      if (data.success) {
        const items = data.data?.items || [];

        setRows(
          items.map((row) => ({
            id: row.module_id,
            ...row,
          }))
        );

        setRowCount(data.data?.total || 0);
      } else {
        setSnackbar({
          open: true,
          message: data.message || "Failed to load modules",
          severity: "error",
        });
      }
    } catch (error) {
      setSnackbar({
        open: true,
        message: error?.response?.data?.message || "Failed to load modules",
        severity: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (clientId && collegeId && clientId !== "null" && collegeId !== "null") {
      fetchData(page, pageSize, search);
    }

    // eslint-disable-next-line
  }, [clientId, collegeId, page, pageSize, search]);

  // ---------------------------------------------------------
  // FORM
  // ---------------------------------------------------------

  const resetForm = () => {
    setForm({
      module_code: "",
      module_name: "",
      icon_name: "",
      route_path: "",
      display_order: 1,
      is_active: true,
    });
  };

  const handleOpen = () => {
    setEditing(null);
    resetForm();
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    setEditing(null);
  };

  const handleEdit = (row) => {
    setEditing(row);

    setForm({
      module_code: row.module_code || "",
      module_name: row.module_name || "",
      icon_name: row.icon_name || "",
      route_path: row.route_path || "",
      display_order: row.display_order || 1,
      is_active: row.is_active ?? true,
    });

    setOpen(true);
  };

  // ---------------------------------------------------------
  // DELETE
  // ---------------------------------------------------------

  const handleDelete = async (row) => {
    const result = await Swal.fire({
      title: "Are you sure?",
      text: "This module will be deactivated.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Yes, delete it!",
      cancelButtonText: "No, keep it!",
    });

    if (!result.isConfirmed) return;

    try {
      await axios.delete(
        `${API_BASE_URL}/api/rbacall/modules/${clientId}/${collegeId}/${row.module_id}`
      );

      await Swal.fire("Deleted!", "Module has been deactivated.", "success");

      fetchData(page, pageSize, search);
    } catch (error) {
      await Swal.fire("Error", error?.response?.data?.message || "Delete failed.", "error");
    }
  };

  // ---------------------------------------------------------
  // SAVE
  // ---------------------------------------------------------

  const handleSubmit = async () => {
    if (!form.module_code.trim()) {
      setSnackbar({
        open: true,
        message: "Module code is required",
        severity: "warning",
      });

      return;
    }

    if (!form.module_name.trim()) {
      setSnackbar({
        open: true,
        message: "Module name is required",
        severity: "warning",
      });

      return;
    }

    try {
      if (editing) {
        await axios.put(
          `${API_BASE_URL}/api/rbacall/modules/${clientId}/${collegeId}/${editing.module_id}`,
          {
            ...form,
            modified_by: user?.user_id || "system",
          }
        );

        setSnackbar({
          open: true,
          message: "Module updated successfully",
          severity: "success",
        });
      } else {
        await axios.post(`${API_BASE_URL}/api/rbacall/modules/${clientId}/${collegeId}`, {
          ...form,
          client_id: clientId,
          college_id: collegeId,
          created_by: user?.user_id || "system",
        });

        setSnackbar({
          open: true,
          message: "Module created successfully",
          severity: "success",
        });
      }

      setOpen(false);

      fetchData(page, pageSize, search);
    } catch (error) {
      setSnackbar({
        open: true,
        message: error?.response?.data?.message || "Save failed",
        severity: "error",
      });
    }
  };

  // ---------------------------------------------------------
  // COLUMNS
  // ---------------------------------------------------------

  const columns = [
    {
      field: "serial",
      headerName: "S. No.",
      width: 80,
      sortable: false,
      filterable: false,
      renderCell: (params) => params.api.getRowIndexRelativeToVisibleRows(params.id) + 1,
    },

    {
      field: "module_code",
      headerName: "Module Code",
      flex: 0.8,
    },

    {
      field: "module_name",
      headerName: "Module Name",
      flex: 1.2,
    },

    {
      field: "route_path",
      headerName: "Route",
      flex: 1.2,
    },

    {
      field: "display_order",
      headerName: "Order",
      width: 100,
    },

    {
      field: "is_active",
      headerName: "Status",
      width: 100,
      renderCell: (params) => (params.value === true ? "Active" : "Inactive"),
    },

    {
      field: "actions",
      headerName: "Actions",
      width: 120,
      sortable: false,
      renderCell: (params) => (
        <ActionsMenu row={params.row} onEdit={handleEdit} onDelete={handleDelete} />
      ),
    },
  ];

  // ---------------------------------------------------------
  // UI
  // ---------------------------------------------------------

  return (
    <Box p={2}>
      <Paper elevation={3} sx={{ p: 2 }}>
        <Toolbar>
          <Typography variant="h5" sx={{ flexGrow: 1 }}>
            RBAC MODULES
          </Typography>

          <TextField
            size="medium"
            placeholder="Search..."
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setPage(0);
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
            sx={{ mr: 2 }}
          />

          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleOpen}
            sx={{ color: "#fff" }}
          >
            Add Module
          </Button>
        </Toolbar>

        <Box mt={2}>
          <DataGrid
            rows={rows}
            columns={columns}
            loading={loading}
            autoHeight
            page={page}
            pageSize={pageSize}
            rowCount={rowCount}
            pagination
            paginationMode="server"
            onPageChange={(newPage) => {
              setPage(newPage);
            }}
            onPageSizeChange={(newPageSize) => {
              setPageSize(newPageSize);
              setPage(0);
            }}
            rowsPerPageOptions={[10, 20, 50, 100]}
            disableSelectionOnClick
          />
        </Box>
      </Paper>

      {/* ------------------------------------------------ */}
      {/* DIALOG */}
      {/* ------------------------------------------------ */}

      <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
        <DialogTitle>{editing ? "Edit Module" : "Add Module"}</DialogTitle>

        <DialogContent>
          <Stack spacing={2} mt={1}>
            <TextField
              label="Module Code"
              value={form.module_code}
              onChange={(e) =>
                setForm({
                  ...form,
                  module_code: e.target.value.toUpperCase(),
                })
              }
              fullWidth
              required
              InputLabelProps={{
                shrink: true,
              }}
            />

            <TextField
              label="Module Name"
              value={form.module_name}
              onChange={(e) =>
                setForm({
                  ...form,
                  module_name: e.target.value,
                })
              }
              fullWidth
              required
              InputLabelProps={{
                shrink: true,
              }}
            />

            <TextField
              label="Module Icon"
              value={form.icon_name}
              onChange={(e) =>
                setForm({
                  ...form,
                  icon_name: e.target.value,
                })
              }
              fullWidth
              InputLabelProps={{
                shrink: true,
              }}
            />

            <TextField
              label="Route Path"
              value={form.route_path}
              onChange={(e) =>
                setForm({
                  ...form,
                  route_path: e.target.value,
                })
              }
              fullWidth
              InputLabelProps={{
                shrink: true,
              }}
            />

            <TextField
              label="Display Order"
              type="number"
              value={form.display_order}
              onChange={(e) =>
                setForm({
                  ...form,
                  display_order: Number(e.target.value),
                })
              }
              fullWidth
              InputLabelProps={{
                shrink: true,
              }}
            />

            <TextField
              select
              label="Status"
              value={form.is_active}
              onChange={(e) =>
                setForm({
                  ...form,
                  is_active: Number(e.target.value),
                })
              }
              fullWidth
              InputLabelProps={{
                shrink: true,
              }}
              SelectProps={{
                native: true,
              }}
            >
              <option value={true}>Active</option>

              <option value={false}>Inactive</option>
            </TextField>
          </Stack>
        </DialogContent>

        <DialogActions>
          <Button onClick={handleClose}>Cancel</Button>

          <Button variant="contained" onClick={handleSubmit} sx={{ color: "#fff" }}>
            {editing ? "Update" : "Create"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ------------------------------------------------ */}
      {/* SNACKBAR */}
      {/* ------------------------------------------------ */}

      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() =>
          setSnackbar({
            ...snackbar,
            open: false,
          })
        }
        anchorOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
      >
        <Alert
          severity={snackbar.severity}
          onClose={() =>
            setSnackbar({
              ...snackbar,
              open: false,
            })
          }
          sx={{ width: "100%" }}
          elevation={6}
          variant="filled"
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
